/**
 * Haptic feedback utility for mobile devices
 * 
 * IMPORTANT LIMITATIONS:
 * - iOS Safari: Does NOT support haptic feedback in web browsers.
 *   Haptics only work on iOS in native apps (via Capacitor) or installed PWAs.
 * - Android Chrome: Supports navigator.vibrate() API.
 */

type HapticStyle = "light" | "medium" | "heavy" | "success" | "warning" | "error";

interface Vibration {
  pattern?: number[];
  duration?: number;
}

const hapticPatterns: Record<HapticStyle, Vibration> = {
  light: { duration: 10 },
  medium: { duration: 20 },
  heavy: { duration: 30 },
  success: { pattern: [10, 50, 10] },
  warning: { pattern: [20, 100, 20] },
  error: { pattern: [30, 100, 30, 100, 30] },
};

/**
 * Trigger haptic feedback on supported devices
 */
export function triggerHaptic(style: HapticStyle = "light"): void {
  // Check if we're on a mobile device
  if (!isMobileDevice()) {
    console.debug('Haptic: Not a mobile device');
    return;
  }

  // Try Android vibration API (works on Android Chrome)
  if (tryAndroidVibration(style)) return;

  // iOS Safari does not support web haptic feedback
  console.debug('Haptic: No supported API available (iOS Safari does not support web haptics)');
}

/**
 * Android Vibration API
 * Works on Android Chrome, does NOT work on iOS Safari
 */
function tryAndroidVibration(style: HapticStyle): boolean {
  if (!('vibrate' in navigator)) {
    console.debug('Haptic: Vibration API not available');
    return false;
  }

  try {
    const vibration = hapticPatterns[style];
    let result: boolean;
    
    if (vibration.pattern) {
      result = navigator.vibrate(vibration.pattern);
    } else if (vibration.duration) {
      result = navigator.vibrate(vibration.duration);
    } else {
      result = false;
    }
    
    console.debug('Haptic triggered:', style, 'result:', result);
    return result;
  } catch (e) {
    console.debug('Vibration API failed:', e);
    return false;
  }
}

/**
 * Check if running on mobile device
 */
function isMobileDevice(): boolean {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/**
 * Trigger haptic on button press
 */
export function hapticButtonPress(): void {
  triggerHaptic("light");
}

/**
 * Trigger haptic for successful action
 */
export function hapticSuccess(): void {
  triggerHaptic("success");
}

/**
 * Trigger haptic for error/warning
 */
export function hapticError(): void {
  triggerHaptic("error");
}

/**
 * Trigger haptic for selection/toggle
 */
export function hapticSelection(): void {
  triggerHaptic("medium");
}
